import type { FlowNodeData } from '$/domains/workFlowV3/type';
import {
  resolvePostTaskConfigByMcpInfo,
  type PostTaskConfig,
} from '../config/postTask.config';
import { getAnnotationWorkflowToolsName } from './systemSourceNode';

type TrackableNodeData = FlowNodeData & {
  nineGridPreset?: {
    apiName?: unknown;
    templateId?: unknown;
  };
};

/**
 * 共用 script_generate 的能力必须携带知识库 ID，避免普通脚本节点误判成视频解析。
 */
function canResolveTrackedPostTask(
  apiName: string | undefined,
  parameters: Record<string, unknown> | undefined,
): boolean {
  if (apiName !== 'script_generate') return Boolean(apiName);
  return parameters?.tool_knowledge_id != null || parameters?.template_id != null;
}

/**
 * 按实际提交参数解析功能执行 tab，确保共用 api_name 的能力仍能精确命中。
 */
export function resolvePostTaskFuncRunTrackTab(
  config: PostTaskConfig,
  parameters: Record<string, unknown>,
): string | undefined {
  const resolvedConfig = resolvePostTaskConfigByMcpInfo(config.api_name, {
    ...config.parameters,
    ...parameters,
  }) ?? config;
  return resolvedConfig.tracking?.funcRunTab;
}

/**
 * 按节点持久化状态解析后链路配置，优先使用九宫格标签和 pre_tools，再回退主工具。
 */
export function resolveNodePostTaskConfig(
  data: FlowNodeData | undefined,
): PostTaskConfig | undefined {
  if (!data) return undefined;
  const trackableData = data as TrackableNodeData;
  const preset = trackableData.nineGridPreset;
  if (typeof preset?.apiName === 'string') {
    const presetConfig = resolvePostTaskConfigByMcpInfo(preset.apiName, {
      tool_knowledge_id: preset.templateId,
    });
    if (presetConfig) return presetConfig;
  }

  const mainToolName = data.mcpInfo?.api_name;
  const preToolConfig = data.pre_tools
    // 同名 pre_tool 是主工具的前置阶段，埋点能力仍由外层知识库 ID 决定。
    ?.filter((tool) => tool.name !== mainToolName)
    ?.filter((tool) => canResolveTrackedPostTask(tool.name, tool.parameters))
    .map((tool) => resolvePostTaskConfigByMcpInfo(tool.name, tool.parameters))
    .find((config) => Boolean(config?.tracking));
  if (preToolConfig) return preToolConfig;

  if (!canResolveTrackedPostTask(data.mcpInfo?.api_name, data.mcpInfo?.parameters)) {
    return undefined;
  }
  return resolvePostTaskConfigByMcpInfo(
    data.mcpInfo?.api_name,
    data.mcpInfo?.parameters,
  );
}

/**
 * 解析底部发送事件的 tools_name；后链路稳定枚举优先于历史标注来源值。
 */
export function resolveWorkflowSendToolsName(data: FlowNodeData | undefined): string {
  const trackName = resolveNodePostTaskConfig(data)?.tracking?.workflowSendToolsName;
  return trackName ?? getAnnotationWorkflowToolsName(data);
}

/**
 * 解析任务成功/失败事件的 tools_name，未配置的新旧能力继续使用原展示名。
 */
export function resolveWorkflowNodeRunToolsName(
  data: FlowNodeData | undefined,
  fallback: string,
): string {
  return resolveNodePostTaskConfig(data)?.tracking?.nodeRunToolsName ?? fallback;
}

/**
 * 判断节点是否来自本次后链路能力，供 source 与节点创建来源字段统一使用。
 */
export function isDownstreamToolNode(data: FlowNodeData | undefined): boolean {
  return Boolean(data?.isBacklink || resolveNodePostTaskConfig(data)?.tracking);
}

/**
 * 将一次执行涉及的 task_id 展开成独立埋点参数，并在单次调用内去重。
 */
export function expandWorkflowNodeRunTrackParams(
  params: Record<string, unknown>,
  taskIds: Array<string | undefined>,
): Record<string, unknown>[] {
  const uniqueTaskIds = Array.from(new Set(taskIds.filter(
    (taskId): taskId is string => typeof taskId === 'string' && taskId !== '',
  )));
  const normalizedTaskIds = uniqueTaskIds.length ? uniqueTaskIds : [''];
  return normalizedTaskIds.map((taskId) => ({
    ...params,
    task_id: taskId,
  }));
}

/**
 * 解析 roboneo_func_run_click 的 input_text：优先显式传入，再从执行参数 prompt 提取。
 */
export function resolveFlowFuncRunInputText(
  paramsJson: Record<string, unknown> | undefined,
  explicitInputText?: string,
): string {
  if (explicitInputText !== undefined) {
    return explicitInputText;
  }
  const prompt = paramsJson?.prompt;
  if (typeof prompt === 'string') {
    return prompt;
  }
  const paramsList = paramsJson?.params_list;
  if (Array.isArray(paramsList)) {
    const prompts = paramsList
      .map((item) => (
        item && typeof item === 'object'
          ? (item as Record<string, unknown>).prompt
          : undefined
      ))
      .filter((item): item is string => typeof item === 'string' && item !== '');
    if (prompts.length) {
      return prompts.join(',');
    }
  }
  return '';
}
